import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import Grid from '@/global/components/ui/Grid'
import { formatPorcentagemRegra, onlyDefined, onlyUnique } from '@acme/common'
import { OpcoesExportacaoFolha as Opcoes } from '@acme/core'
import {
    FormField,
    FormFieldAutocomplete,
    FormFieldSelect,
} from 'global/components/Form'
import { useAuth } from 'hooks'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { regrasCalculo } from 'service/api'

const NOME_PORCENTAGENS = 'porcentagensSelecionadas'

export default function FormExportacaoTxt({
    podeLancarPagamento,
}: {
    podeLancarPagamento: boolean
}) {
    const { watch, setValue } = useFormContext()
    const [tipoExtras, tipoFaltas]: [number, number] = watch([
        'horasExtras',
        'horasFaltas',
    ])
    const {
        decoded: { cliente: clienteId },
    } = useAuth()
    const { data: regras } = regrasCalculo.useFetch({
        queryParams: {
            clienteId,
        },
    })
    const porcentagens =
        regras
            ?.map(item => item.porcentagens.map(p => p.porcentagem))
            .flat()
            .filter(onlyUnique)
            .filter(onlyDefined)
            .sort((a, b) => String(a).localeCompare(String(b))) ?? []

    /** O campo guarda a lista serializada — o schema espera uma string JSON. */
    function desserializar(serializado: unknown): typeof porcentagens {
        if (typeof serializado !== 'string' || !serializado) return []
        try {
            const lista = JSON.parse(serializado)
            return Array.isArray(lista) ? lista : []
        } catch {
            return []
        }
    }

    useEffect(() => {
        if (tipoExtras == Opcoes.NADA) {
            setValue(NOME_PORCENTAGENS, '[]')
        } else if (tipoExtras == Opcoes.PAGAR) {
            setValue(NOME_PORCENTAGENS, JSON.stringify(porcentagens))
        }
    }, [tipoExtras])

    return (
        <>
            <HorizontalDivider className="mb-4">Horas extras</HorizontalDivider>
            <Grid container>
                <Grid item xs={tipoExtras == Opcoes.DESCONTAR ? 10 : 12}>
                    <FormFieldSelect
                        name="horasExtras"
                        options={[
                            {
                                label: 'Apenas gerar o arquivo de exportação',
                                value: Opcoes.NADA.toString(),
                            },
                            {
                                label: 'Lançar pagamento para as horas exportadas',
                                value: Opcoes.PAGAR.toString(),
                                disabled: !podeLancarPagamento,
                            },
                            {
                                label: 'Lançar pagamento para as primeiras X horas',
                                value: Opcoes.DESCONTAR.toString(),
                                disabled: !podeLancarPagamento,
                            },
                        ]}
                    />
                </Grid>
                {tipoExtras == Opcoes.DESCONTAR && (
                    <Grid item xs={2}>
                        <FormField
                            name="horasExtrasPagar"
                            label="Horas"
                            autoComplete="off"
                            options={{
                                blocks: [3, 2],
                                delimiter: ':',
                                numericOnly: true,
                            }}
                        />
                    </Grid>
                )}
                {tipoExtras != Opcoes.NADA && (
                    <Grid item xs={12}>
                        <FormFieldAutocomplete
                            multiple
                            name={NOME_PORCENTAGENS}
                            label="Porcentagens a pagar"
                            placeholder="Selecione as porcentagens"
                            // Chips curtos: cabem todos na linha, e ver o que
                            // será exportado importa mais que o resumo.
                            limitTags={6}
                            options={porcentagens}
                            getOptionLabel={item =>
                                formatPorcentagemRegra(item)
                            }
                            paraCampo={desserializar}
                            paraFormulario={selecionadas =>
                                JSON.stringify(selecionadas)
                            }
                            noOptionsText="Nenhuma porcentagem encontrada"
                        />
                    </Grid>
                )}
            </Grid>
            <HorizontalDivider className="mt-6 mb-4">Faltas</HorizontalDivider>
            <Grid container>
                <Grid item xs={tipoFaltas == Opcoes.DESCONTAR ? 10 : 12}>
                    <FormFieldSelect
                        name="horasFaltas"
                        options={[
                            {
                                label: 'Apenas gerar o arquivo de exportação',
                                value: Opcoes.NADA.toString(),
                            },
                            {
                                label: 'Lançar pagamento para todas as horas exportadas',
                                value: Opcoes.PAGAR.toString(),
                                disabled: !podeLancarPagamento,
                            },
                            {
                                label: 'Lançar pagamento para as primeiras X horas',
                                value: Opcoes.DESCONTAR.toString(),
                                disabled: !podeLancarPagamento,
                            },
                        ]}
                    />
                </Grid>
                {tipoFaltas == Opcoes.DESCONTAR && (
                    <Grid item xs={2}>
                        <FormField
                            name="horasFaltasPagar"
                            label="Horas"
                            autoComplete="off"
                            options={{
                                blocks: [3, 2],
                                delimiter: ':',
                                numericOnly: true,
                            }}
                        />
                    </Grid>
                )}
            </Grid>
        </>
    )
}
